import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { InputField, SelectField, TextareaField } from './form-controls';
import { SectionShell, FieldGrid } from './section-shell';
import { CONTRACT_ROLE_OPTIONS, CONTRACT_DIRECTION_OPTIONS } from '@/pages/contracts/contract-format';
import type { ContractFormValues } from './contract-form';

/**
 * Agreement terms — contract role, direction, value, and internal note. The
 * `supplier` role is gated on the selected contact's roles (legacy
 * `contractRoleOptions` filter); if the current role becomes unavailable it
 * falls back to the first allowed option.
 */
export function AgreementSection({ contactRoles }: { contactRoles: string[] }) {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const role = useWatch({ control, name: 'contract_role' });

  const roleOptions = CONTRACT_ROLE_OPTIONS.filter(
    (o) => o.value !== 'supplier' || contactRoles.includes('supplier'),
  );

  // Keep the selected role within the allowed set (legacy fallback effect).
  useEffect(() => {
    if (roleOptions.some((o) => o.value === role)) return;
    if (roleOptions[0]) setValue('contract_role', roleOptions[0].value);
  }, [role, roleOptions, setValue]);

  return (
    <SectionShell title="Agreement" description="Role, direction, and notes for this contract.">
      <FieldGrid>
        <SelectField
          name="contract_role"
          label="Role"
          options={roleOptions}
          placeholder="Select a role"
          required
        />
        <SelectField
          name="contract_direction"
          label="Direction"
          options={CONTRACT_DIRECTION_OPTIONS}
          placeholder="Select a direction"
          required
        />
        <InputField name="contract_value" label="Contract value" type="number" placeholder="0" />
      </FieldGrid>

      <TextareaField
        name="contract_note"
        label="Note"
        placeholder="Optional internal note about this contract."
      />
    </SectionShell>
  );
}
